/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/audits/run-all-button.tsx` (verbatim, minus the inert
 * `"use client"`). No `next/*`, no server sink.
 *
 * ⚠️ `onRunAll` is a CALLBACK prop, and in the Next tree the caller passed a
 * server action. In this host the caller has to pass something that goes through
 * `apiCall` against an `Operation` instead — the component itself is agnostic,
 * which is why it ports cleanly, but the surface that mounts it is not done until
 * that endpoint exists. `src/audits/*` has no mounted audit-run contract on the
 * Hono host today.
 */

import { useState, useTransition } from "react";
import { Button, cn } from "@excellent/ui";
import { RotateCwIcon } from "@excellent/ui/icons";

/**
 * The `/audits` hub's one-click "run every due audit" control. What it calls
 * spawns one read-only coding-agent run per due function; this just reflects the
 * in-flight state and surfaces a spawn error inline. Disabled (and relabelled)
 * when nothing is due so the page reads as "all current".
 */
export function RunAllAuditsButton({
  dueCount,
  onRunAll,
}: {
  dueCount: number;
  onRunAll: () => Promise<void>;
}) {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function run() {
    if (pending || dueCount === 0) return;
    setError(null);
    startTransition(async () => {
      try {
        await onRunAll();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to start audits");
      }
    });
  }

  return (
    <div className="flex items-center gap-3">
      {error ? <span className="type-body text-destructive">{error}</span> : null}
      <Button type="button" onClick={run} disabled={pending || dueCount === 0}>
        <RotateCwIcon aria-hidden="true" className={cn(pending && "animate-spin")} />
        {pending
          ? "Starting…"
          : dueCount === 0
            ? "All audits current"
            : `Run ${dueCount} due audit${dueCount === 1 ? "" : "s"}`}
      </Button>
    </div>
  );
}
